import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { DRAFT_TYPES, confBadge, riskBadge } from "../lib/constants";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkle, Check, X, CircleNotch, ShieldWarning } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

export function AIPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [engId, setEngId] = useState("");
  const [draftType, setDraftType] = useState("client_email");

  const { data: actions } = useQuery({
    queryKey: ["ai-actions", "pending"],
    queryFn: () => api.get("/ai/actions?status=pending").then((r) => r.data),
    refetchInterval: 15000,
  });
  const { data: engagements } = useQuery({ queryKey: ["engagements"], queryFn: () => api.get("/engagements").then((r) => r.data) });

  const draft = useMutation({
    mutationFn: () => api.post("/ai/draft", { engagement_id: engId, draft_type: draftType }),
    onSuccess: () => { toast.success("AI draft created — review below"); qc.invalidateQueries({ queryKey: ["ai-actions"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const review = useMutation({
    mutationFn: ({ id, verb }) => api.post(`/ai/actions/${id}/${verb}`, {}),
    onSuccess: (_, v) => { toast.success(v.verb === "approve" ? "Approved" : "Rejected"); qc.invalidateQueries(); },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <aside data-testid="ai-panel" className="hidden xl:flex w-80 shrink-0 flex-col border-l border-zinc-200 bg-zinc-50 h-screen sticky top-0 overflow-y-auto">
      <div className="p-4 border-b border-zinc-200">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={16} weight="fill" className="text-zinc-900" />
          <h3 className="font-heading font-medium text-sm text-zinc-900">AI Chief-of-Staff</h3>
        </div>
        <p className="text-xs text-zinc-500">Claude Sonnet 4.5 · Circular 230 guardrails</p>
      </div>

      <div className="p-4 border-b border-zinc-200 space-y-2">
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Generate draft</p>
        <Select value={engId} onValueChange={setEngId}>
          <SelectTrigger data-testid="ai-panel-engagement-select" className="h-8 text-xs bg-white"><SelectValue placeholder="Select engagement" /></SelectTrigger>
          <SelectContent>
            {(engagements || []).filter((e) => e.stage !== "archived").map((e) => (
              <SelectItem key={e.id} value={e.id} className="text-xs">{e.client_name} — {e.service_type} {e.tax_year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={draftType} onValueChange={setDraftType}>
          <SelectTrigger data-testid="ai-panel-drafttype-select" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DRAFT_TYPES.map((d) => <SelectItem key={d.key} value={d.key} className="text-xs">{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="ai-panel-generate-btn" size="sm" className="w-full h-8 text-xs" disabled={!engId || draft.isPending} onClick={() => draft.mutate()}>
          {draft.isPending ? <><CircleNotch size={14} className="animate-spin mr-1" /> Drafting…</> : <><Sparkle size={14} className="mr-1" /> Generate</>}
        </Button>
      </div>

      <div className="p-4 space-y-3 flex-1">
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Pending approvals ({actions?.length || 0})</p>
        {(actions || []).map((a) => (
          <div key={a.id} data-testid="ai-panel-action-card" className="rounded-md border border-zinc-200 bg-white p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-900">{DRAFT_TYPES.find((d) => d.key === a.draft_type)?.label || a.draft_type}</span>
              {a.requires_cpa_approval && <ShieldWarning size={14} className="text-amber-600" title="Requires CPA" />}
            </div>
            <p className="text-xs text-zinc-500">{a.client_name}</p>
            <div className="flex gap-1.5">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${confBadge(a.output?.confidence)}`}>conf {Math.round((a.output?.confidence || 0) * 100)}%</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${riskBadge(a.output?.risk_score)}`}>risk {Math.round((a.output?.risk_score || 0) * 100)}%</span>
            </div>
            <p className="text-xs text-zinc-600 line-clamp-3">{a.output?.client_facing_text}</p>
            <div className="flex gap-1.5">
              <Button data-testid="ai-panel-approve-btn" size="sm" variant="outline" className="h-7 text-xs flex-1"
                disabled={review.isPending || (a.requires_cpa_approval && user?.role !== "cpa")}
                onClick={() => review.mutate({ id: a.id, verb: "approve" })}>
                <Check size={12} className="mr-1" /> Approve
              </Button>
              <Button data-testid="ai-panel-reject-btn" size="sm" variant="ghost" className="h-7 text-xs" disabled={review.isPending}
                onClick={() => review.mutate({ id: a.id, verb: "reject" })}>
                <X size={12} />
              </Button>
            </div>
            {a.requires_cpa_approval && user?.role !== "cpa" && <p className="text-[10px] text-amber-600">CPA approval required</p>}
          </div>
        ))}
        {actions?.length === 0 && <p className="text-xs text-zinc-400 italic">No pending drafts. Generate one above.</p>}
      </div>
    </aside>
  );
}
